import { useEffect, useRef, useState } from 'react';
import type { QrPosition, SessionSettings, SessionSettingsPatch } from '@wallflower/shared';
import {
  MAX_TICKER_LIMIT,
  MIN_TICKER_LIMIT,
  QR_SIZES,
  TICKER_SPEEDS,
  TRANSITIONS,
} from '@wallflower/shared';

interface Props {
  settings: SessionSettings;
  onChange: (patch: SessionSettingsPatch) => void;
  /** Video is a property of the plan on some walls, not something the host picks. */
  videoLocked?: boolean;
}

const CORNERS: { value: QrPosition; label: string }[] = [
  { value: 'top-left', label: 'Top left' },
  { value: 'top-right', label: 'Top right' },
  { value: 'bottom-left', label: 'Bottom left' },
  { value: 'bottom-right', label: 'Bottom right' },
];

export default function SettingsPanel({ settings, onChange, videoLocked = false }: Props) {
  return (
    <div className="grid gap-6">
      <section className="panel p-5">
        <h2 className="eyebrow">The screen</h2>

        <Row label="Seconds per photo" value={`${Math.round(settings.slideDurationMs / 1000)}s`}>
          <input
            type="range"
            min={2}
            max={30}
            step={1}
            value={Math.round(settings.slideDurationMs / 1000)}
            onChange={(e) => onChange({ slideDurationMs: Number(e.target.value) * 1000 })}
            className="slider"
            aria-label="Seconds per photo"
          />
        </Row>

        <Row label="Transition">
          <div className="flex flex-wrap gap-2">
            {TRANSITIONS.map((option) => (
              <Chip
                key={option}
                active={settings.transition === option}
                onClick={() => onChange({ transition: option })}
              >
                {option === 'kenburns' ? 'Slow zoom' : option === 'slide' ? 'Slide' : 'Fade'}
              </Chip>
            ))}
          </div>
        </Row>

        <Toggle
          label="Shuffle the order"
          hint="Off plays oldest first, so the night unfolds in order."
          checked={settings.shuffle}
          onChange={(shuffle) => onChange({ shuffle })}
        />
        <Toggle
          label="New photos jump the queue"
          hint="A guest sees their upload while they're still holding the phone."
          checked={settings.newMediaPriority}
          onChange={(newMediaPriority) => onChange({ newMediaPriority })}
        />
        <Toggle
          label="Show who sent it"
          hint="Displays the name a guest typed, if they gave one."
          checked={settings.showCaptions}
          onChange={(showCaptions) => onChange({ showCaptions })}
        />
      </section>

      <section className="panel p-5">
        <h2 className="eyebrow">The QR card</h2>

        <Row label="Where it sits">
          <div className="grid max-w-[280px] grid-cols-2 gap-2">
            {CORNERS.map((corner) => (
              <Chip
                key={corner.value}
                active={settings.qrPosition === corner.value}
                onClick={() => onChange({ qrPosition: corner.value })}
              >
                {corner.label}
              </Chip>
            ))}
            <Chip
              active={settings.qrPosition === 'hidden'}
              onClick={() => onChange({ qrPosition: 'hidden' })}
            >
              Hide it
            </Chip>
          </div>
        </Row>

        <Row label="Card size">
          <div className="flex gap-2">
            {QR_SIZES.map((size) => (
              <Chip
                key={size}
                active={settings.qrSize === size}
                onClick={() => onChange({ qrSize: size })}
              >
                {size === 'sm' ? 'Small' : size === 'md' ? 'Medium' : 'Large'}
              </Chip>
            ))}
          </div>
        </Row>

        <Row label="What the card says">
          <DebouncedInput
            value={settings.qrPrompt}
            maxLength={120}
            placeholder="Scan to add your photos!"
            onCommit={(qrPrompt) => onChange({ qrPrompt })}
          />
        </Row>
      </section>

      <section className="panel p-5">
        <h2 className="eyebrow">The ticker</h2>

        <Toggle
          label="Scroll messages along the bottom"
          hint="Turn off to keep the screen to photos alone."
          checked={settings.showTicker}
          onChange={(showTicker) => onChange({ showTicker })}
        />
        <Toggle
          label="Guests can add messages"
          hint="Your own announcements still show when this is off."
          checked={settings.allowMessages}
          onChange={(allowMessages) => onChange({ allowMessages })}
        />

        <Row label="Scroll speed">
          <div className="flex gap-2">
            {TICKER_SPEEDS.map((option) => (
              <Chip
                key={option}
                active={settings.tickerSpeed === option}
                onClick={() => onChange({ tickerSpeed: option })}
              >
                {option === 'slow' ? 'Slow' : option === 'fast' ? 'Fast' : 'Normal'}
              </Chip>
            ))}
          </div>
        </Row>

        <Row label="Messages in one loop" value={String(settings.tickerLimit)}>
          <input
            type="range"
            min={MIN_TICKER_LIMIT}
            max={MAX_TICKER_LIMIT}
            step={5}
            value={settings.tickerLimit}
            onChange={(e) => onChange({ tickerLimit: Number(e.target.value) })}
            className="slider"
            aria-label="Messages in one loop"
          />
        </Row>
      </section>

      <section className="panel p-5">
        <h2 className="eyebrow">What guests can send</h2>

        <Toggle
          label="Allow short videos"
          hint={
            videoLocked
              ? 'Demo walls are photos only.'
              : 'Off means photos only, and the upload page stops offering video.'
          }
          checked={settings.allowVideo}
          disabled={videoLocked}
          onChange={(allowVideo) => onChange({ allowVideo })}
        />

        {settings.allowVideo ? (
          <Row label="Longest clip" value={`${settings.maxVideoSeconds}s`}>
            <input
              type="range"
              min={3}
              max={60}
              step={1}
              value={settings.maxVideoSeconds}
              onChange={(e) => onChange({ maxVideoSeconds: Number(e.target.value) })}
              className="slider"
              aria-label="Longest clip in seconds"
            />
          </Row>
        ) : null}
      </section>
    </div>
  );
}

function Row({
  label,
  value,
  children,
}: {
  label: string;
  value?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mt-5 first:mt-4">
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <span className="text-sm text-chalk">{label}</span>
        {value ? <span className="font-display text-sm text-sulphur">{value}</span> : null}
      </div>
      {children}
    </div>
  );
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className="rounded-lg border px-3 py-2 text-sm transition-colors"
      style={{
        borderColor: active ? 'var(--color-petal)' : 'var(--color-edge)',
        backgroundColor: active ? 'color-mix(in oklab, var(--color-petal) 18%, transparent)' : 'transparent',
        color: active ? 'var(--color-chalk)' : 'var(--color-muted)',
      }}
    >
      {children}
    </button>
  );
}

function Toggle({
  label,
  hint,
  checked,
  disabled = false,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label
      className={`mt-5 flex items-start gap-3 ${disabled ? 'opacity-55' : 'cursor-pointer'}`}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="switch mt-0.5"
      />
      <span>
        <span className="block text-sm text-chalk">{label}</span>
        {hint ? <span className="block text-xs text-muted">{hint}</span> : null}
      </span>
    </label>
  );
}

/** Typing shouldn't fire a request per keystroke, but the TV should feel live. */
function DebouncedInput({
  value,
  onCommit,
  ...rest
}: {
  value: string;
  onCommit: (value: string) => void;
} & React.InputHTMLAttributes<HTMLInputElement>) {
  const [draft, setDraft] = useState(value);
  const dirtyRef = useRef(false);

  useEffect(() => {
    if (!dirtyRef.current) setDraft(value);
  }, [value]);

  useEffect(() => {
    if (!dirtyRef.current) return;
    const timer = setTimeout(() => {
      dirtyRef.current = false;
      onCommit(draft);
    }, 400);
    return () => clearTimeout(timer);
  }, [draft, onCommit]);

  return (
    <input
      {...rest}
      className="field"
      value={draft}
      onChange={(e) => {
        dirtyRef.current = true;
        setDraft(e.target.value);
      }}
    />
  );
}
